import { ArrowLeft, Download } from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { FORM_SETTINGS_SECTIONS } from "../index";
import type { FormSettingsSection } from "../types";
import { FormSettingsContent } from "./form-settings-content";
import { useTranslations } from "next-intl";
import { SettingsSearch } from "./settings-search";

interface FormSettingsMobileLayoutProps {
	activeSection: FormSettingsSection;
	exportDisabled: boolean;
	exportLoading: boolean;
	onExportClick: () => void;
	onSectionChange: (section: FormSettingsSection) => void;
	onClose: () => void;
	sectionProps: any;
}

export function FormSettingsMobileLayout({
	activeSection,
	exportDisabled,
	exportLoading,
	onExportClick,
	onSectionChange,
	onClose,
	sectionProps,
}: FormSettingsMobileLayoutProps) {

	const t = useTranslations("formSettings");	const [showSectionList, setShowSectionList] = useState(true);

	const handleSectionClick = (sectionId: FormSettingsSection) => {
		if (sectionId === "design" && sectionProps?.formId) {
			const url = `/form-builder/${sectionProps.formId}/customize`;
			window.open(url, "_blank", "noopener,noreferrer");
			return;
		}
		onSectionChange(sectionId);
		setShowSectionList(false);
	};

	const handleBackClick = () => {
		setShowSectionList(true);
	};

	if (showSectionList) {
		return (
			<div className="flex h-full min-h-0 flex-col gap-4 md:hidden">
				<div className="p-2">
					<SettingsSearch
						activeSection={activeSection}
						onSectionChange={onSectionChange}
					/>
				</div>
				<ScrollArea className="min-h-0 flex-1">
					<nav
						aria-label={t("settingsSections")}
						className="flex flex-col gap-1 overflow-auto p-2"
						role="navigation"
					>
						{FORM_SETTINGS_SECTIONS.map((section, index) => (
							<button
								aria-label={t("goToSection", { label: t(`sections.${section.id}`) })}
								className="flex w-full items-center justify-between rounded-md px-4 py-4 text-left font-medium text-sm transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground"
								key={section.id}
								onClick={() => handleSectionClick(section.id)}
								onKeyDown={(e) => {
									if (e.key === "Enter" || e.key === " ") {
										e.preventDefault();
										handleSectionClick(section.id);
									}
									if (
										e.key === "ArrowDown" &&
										index < FORM_SETTINGS_SECTIONS.length - 1
									) {
										e.preventDefault();
										const nextButton = e.currentTarget.parentElement?.children[
											index + 1
										] as HTMLElement;
										nextButton?.focus();
									}
									if (e.key === "ArrowUp" && index > 0) {
										e.preventDefault();
										const prevButton = e.currentTarget.parentElement?.children[
											index - 1
										] as HTMLElement;
										prevButton?.focus();
									}
								}}
								role="menuitem"
								tabIndex={0}
							>
								<span>{t(`sections.${section.id}`)}</span>
								<ArrowLeft aria-hidden="true" className="size-4 rotate-180" />
							</button>
						))}
					</nav>
					<div className="border-border border-t px-2 pt-3 pb-2">
						<Button
							aria-label={t("exportData")}
							className="w-full"
							disabled={exportDisabled}
							onClick={onExportClick}
							size="sm"
							variant="outline"
						>
							<Download aria-hidden="true" className="size-4" />
							{exportLoading ? t("preparing") : t("exportSecure")}
						</Button>
					</div>
				</ScrollArea>
			</div>
		);
	}

	return (
		<div className="flex h-full min-h-0 flex-col gap-4 md:hidden">
			<header className="flex flex-shrink-0 items-center gap-3">
				<Button
					aria-label={t("goBackToList")}
					onClick={handleBackClick}
					size="icon"
					variant="outline"
				>
					<ArrowLeft aria-hidden="true" className="size-4" />
				</Button>
				<h2 className="font-semibold text-lg">{t("settings")}</h2>
			</header>
			<main
				aria-labelledby="settings-section-title"
				className="min-h-0 flex-1"
				role="main"
				style={{
					overscrollBehavior: "contain",
				}}
			>
				<ScrollArea className="h-full w-full pb-4">
					<FormSettingsContent
						section={activeSection}
						{...sectionProps}
						updateNotifications={sectionProps.updateNotifications}
					/>
				</ScrollArea>
			</main>
		</div>
	);
}
